import React, { FunctionComponent } from 'react';
import { useIsLargeScreen } from 'scenes/common/hooks/useMedia';

import { TextSmall } from 'components/chroma/extended';
import {
  FooterTop as CommonFooterTop,
  HassleFreeClaimsContent,
  totalLossSettlementHeading,
} from 'scenes/common/components/Footer';
import {
  isBusinessError,
  isIneligiblePromoCodeError,
  isLoadingWithNoData,
  isPromoCodeError,
  isQuoteSaved,
} from 'scenes/common/helpers/apiData';
import { tAndCInsDiscountUrl } from 'scenes/common/helpers/contacts';
import {
  DEFAULT_ONLINE_CAMPAIGN,
  combinedPolicyDiscount,
  insuranceDiscount,
} from 'scenes/common/helpers/insuranceInfo';
import { toPercentage } from 'scenes/common/helpers/percentage';
import { isBeforeEffectiveEndDate } from 'scenes/common/selectors/configSelectors';
import { InsuranceName } from 'scenes/common/types';
import {
  shouldShowClaimCVP,
  showFlybuysInfoSection,
  showFooterMarketingCampaignInformation,
  showFooterQuoteInformation,
  showHassleFreeClaims,
  showSaveOnlineInformation,
} from 'scenes/landlord/components/helpers';
import { informationLinks } from 'scenes/landlord/helpers/informationLinks';
import {
  getCampaign,
  getPolicyStartDate,
  getQuoteNumber,
} from 'scenes/landlord/selectors';
import {
  LandlordFullPageProps,
  LandlordFunnelState,
} from 'scenes/landlord/types';
import {
  listItems,
  supportFootnote,
  totalLossSettlementText,
} from './HassleFreeClaims';

const displayHassleFreeClaimsComp = (
  isLargeScreen: boolean,
  funnelState: LandlordFunnelState
) =>
  isLargeScreen && shouldShowClaimCVP(funnelState)
    ? [
        {
          text: 'How we settle a total loss claim',
          component: totalLossSettlementText,
        },
      ]
    : [
        {
          text: 'Our claims support',
          component: (
            <HassleFreeClaimsContent
              supportBenefitsList={listItems}
              totalLossClaimsWording={totalLossSettlementText}
              totalLossClaimsHeading={totalLossSettlementHeading()}
              supportFootnote={supportFootnote}
            />
          ),
        },
      ];

export const Footer: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState'
>> = ({ funnelState }) => {
  const isLargeScreen = useIsLargeScreen();
  const hasBusinessErrors =
    isBusinessError(funnelState.apiResponses.quoteDetails) &&
    !isPromoCodeError(funnelState.apiResponses.quoteDetails) &&
    !isIneligiblePromoCodeError(funnelState.apiResponses.quoteDetails);

  const isApiQuoteSaved = isQuoteSaved(funnelState.apiResponses.quoteDetails);

  const showQuoteInformation = !hasBusinessErrors && !isApiQuoteSaved;

  const isPageNotLoadingOrError =
    !hasBusinessErrors &&
    !isApiQuoteSaved &&
    !isLoadingWithNoData(funnelState.apiResponses.quoteDetails);

  return (
    <CommonFooterTop
      quoteNumber={getQuoteNumber(funnelState)}
      campaign={getCampaign(funnelState)}
      showQuoteInformation={showQuoteInformation}
      pageNotLoadingOrError={isPageNotLoadingOrError}
      showFlybuys={showFlybuysInfoSection(funnelState.step)}
      showFooterQuoteInformation={showFooterQuoteInformation(
        funnelState.flow,
        funnelState.step
      )}
      showMarketingCampaign={showFooterMarketingCampaignInformation(
        funnelState.flow
      )}
      additionalComponents={[
        ...(showHassleFreeClaims(funnelState.flow, funnelState.step)
          ? displayHassleFreeClaimsComp(isLargeScreen, funnelState)
          : []),
      ]}
      informationLinks={informationLinks}
      insuranceName={InsuranceName.Landlord}
      saveOnlineInformation={
        showSaveOnlineInformation(funnelState) ? (
          <>
            <TextSmall weight={700} margin={{ bottom: 1 }}>
              Save {toPercentage(insuranceDiscount(InsuranceName.Landlord))}{' '}
              online
            </TextSmall>
            <TextSmall weight={600} margin={{ bottom: 4 }}>
              {toPercentage(insuranceDiscount(InsuranceName.Landlord))} off
              Coles Combined Landlord Building & Contents Insurance when you buy
              online.*
            </TextSmall>
            <TextSmall>
              *The {toPercentage(insuranceDiscount(InsuranceName.Landlord))}{' '}
              discount is made up of the{' '}
              {toPercentage(DEFAULT_ONLINE_CAMPAIGN.discount)} online discount
              and a{' '}
              {toPercentage(combinedPolicyDiscount(InsuranceName.Landlord))}{' '}
              combined policy discount. Minimum premiums may apply. This
              discount offer may be withdrawn at any time. Applies to new
              policies only. For full terms and conditions, see{' '}
              <a
                href={tAndCInsDiscountUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              .
            </TextSmall>
          </>
        ) : undefined
      }
      isBeforeEffectiveEndDate={isBeforeEffectiveEndDate(
        funnelState.apiResponses.config,
        getPolicyStartDate(funnelState)
      )}
    />
  );
};

export default Footer;
